import { FC, useContext, useRef } from 'react';

import { AddImageIcon, H3, Tooltip } from '@smartshell/ui-kit';
import cn from 'classnames';

import { useUploadFile } from 'hooks';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';

import { IPlayScreenshotsProps } from './interfaces';
import s from './PlayScreenshots.module.scss';
import { ScreenshotItem } from './ScreenshotItem';
import { TooltipScreenshots } from './TooltipScreenshots';

export const PlayScreenshots: FC<IPlayScreenshotsProps> = ({
  value = [],
  onChange,
  maxCount = 5,
}) => {
  const { t } = useContext(LocalizationContext);
  const imgInput = useRef<HTMLInputElement>(null);
  const FILE_MAXIMUM_SIZE = 1000000;

  const { uploadImages } = useUploadFile(undefined, { multiple: true });

  const remaining = maxCount - value.length;

  return (
    <div className={s.container}>
      <div className={s.header}>
        <div className={s.header_title}>
          <H3>{t('application.screen')}</H3>
          <Tooltip
            contentClassName={s.tooltip}
            content={<TooltipScreenshots />}
          />
        </div>
        <span className={s.image_count}>
          {value.length}/{maxCount}
        </span>
      </div>

      <div className={s.image_block}>
        <div className={s.preview_block}>
          {value.map((src, index) => (
            <ScreenshotItem
              key={src}
              value={src}
              onDelete={() => {
                const next = value.filter((_, i) => i !== index);
                onChange?.(next);
              }}
              onReplace={(newUrl) => {
                const next = [...value];
                next[index] = newUrl;
                onChange?.(next);
              }}
            />
          ))}
        </div>

        {value.length < maxCount && (
          <>
            <div
              className={s.default_image}
              onClick={() => imgInput.current?.click()}
            >
              <AddImageIcon size="m" color="secondary" />
              <span className={cn(s.text, s.text_big)}>
                {t('application.uploadImage')}
              </span>
              <span className={s.text}>
                {t('application.screenshots.default')}
              </span>
            </div>

            <input
              ref={imgInput}
              type="file"
              multiple
              accept=".png,.jpg,.jpeg"
              hidden
              onChange={async (e) => {
                const files = Array.from(e.target.files ?? []).slice(
                  0,
                  remaining
                );
                e.target.value = '';

                if (!files.length || !uploadImages) return;

                if (files.some((fl) => fl.size > FILE_MAXIMUM_SIZE)) {
                  return notification.info({
                    t,
                    type: 'error',
                    title: t('upload.file.error'),
                    message: t('upload.file.error_2'),
                  });
                }

                try {
                  const urls = await uploadImages(files);
                  onChange?.([...value, ...urls]);
                } catch (err) {
                  notification.info({
                    t,
                    type: 'error',
                    title: t('upload.file.error'),
                    message: String(err),
                  });
                }
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};
